import React, { useState, createContext, useContext } from 'react';
import OpenAI from 'openai';
interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}
interface PredefinedQuestion {
  id: string;
  icon: string;
  question: string;
  category: string;
}
interface AIAssistantChatContextType {
  isOpen: boolean;
  messages: Message[];
  isLoading: boolean;
  openChat: () => void;
  closeChat: () => void;
  toggleChat: () => void;
  sendMessage: (content: string) => Promise<void>;
  clearMessages: () => void;
  predefinedQuestions: PredefinedQuestion[];
}
const AIAssistantChatContext = createContext<AIAssistantChatContextType | undefined>(undefined);
const SYSTEM_PROMPT = `Ești asistentul AI oficial al platformei Cautlocuință.ro, o agenție imobiliară profesională din România specializată în închirieri rezidențiale.

DESPRE CAUTLOCUINȚĂ.RO:
- Agenție imobiliară cu focus pe închirieri în București și împrejurimi
- Platformă de training completă pentru agenți: pro.cautlocuinta.ro
- Sistem CRM propriu pentru gestionarea proprietăților și clienților
- Suport dedicat: WhatsApp Business și telefon 0733355359 (09:00-19:00)
- Grup WhatsApp pentru agenți activi - comunicare zilnică

STRUCTURA COMISIOANELOR - FOARTE IMPORTANT:
Pentru PROPRIETARI:
- Comision: 50% din prima lună de chirie + TVA (21%)
- Exemplu: Chirie 800€ → Comision 400€ + 84€ TVA = 484€ total
- Se plătește DOAR după semnarea contractului de închiriere
- Factura se emite de departamentul financiar

Pentru CHIRIAȘI:
- Comision: 50% din prima lună de chirie + TVA (21%)
- Exemplu: Chirie 600€ → Comision 300€ + 63€ TVA = 363€ total
- Include: consultanță, programare vizionări, negociere, verificare documente, asistență semnare

STRUCTURA DE CARIERĂ:
1. Agent Junior (0-5 tranzacții)
   - Început de carieră, învățare intensivă
   - Acces la toate cursurile și training-urile
   - Suport dedicat pentru fiecare tranzacție

2. Agent (6-15 tranzacții)
   - Autonomie crescută
   - Bonusuri de performanță
   - Acces la liste de prospectare prioritare

3. Agent Senior (16-30 tranzacții)
   - Expertiza validată
   - Bonusuri majorate
   - Posibilitate de mentorat pentru agenți junior

4. Team Leader (31+ tranzacții)
   - Leadership și coordonare echipă
   - Cele mai mari bonusuri
   - Participare la decizii strategice

PROCEDURI DE PROSPECTARE:
1. PREGĂTIRE APEL:
   - Vizualizezi complet anunțul (text, poze, preț, zonă)
   - Notezi detalii importante (etaj, suprafață, dotări)
   - Verifici în CRM dacă există deja (caută după telefon)
   - Pregătești fișă de apel pentru notițe

2. VERIFICARE CRM:
   - Caută numărul de telefon în sistem
   - Dacă există colaborare activă (status roșu) → NU contacta
   - Dacă nu există sau status inactiv → poți apela

3. SCRIPTUL DE APEL:
   - Introducere naturală și caldă
   - Explici că ai clienți activi în căutare
   - Nu forțezi colaborarea, oferi ajutor
   - Menționezi comisionul: 50% + TVA doar la închiriere reușită
   - Notezi toate detaliile suplimentare

4. STATUSURI POSIBILE:
   - ✅ Colaborează (necesită ID CRM obligatoriu!)
   - ❌ Nu colaborează
   - 📞 Nu răspunde / mesaj trimis
   - ⚠️ Număr invalid
   - 🏠 A închiriat deja
   - 💼 Dorește vânzare
   - 👥 Colaborare coleg CLC
   - 💬 Oferă comision redus (cere derogare)

5. INDEXARE ÎN CRM:
   - Doar dacă proprietarul colaborează
   - Introduci toate datele proprietății
   - Sistemul generează ID unic (ex: P14433)
   - Copiezi ID-ul în Google Sheet
   - Bifezi "Colaborare Preluată"

CURSURI DISPONIBILE:
1. Curs Introductiv - Fundamentele
   - Despre CautLocuinta.ro și cultura organizațională
   - Structura carierei și comisioanele
   - Recorduri și realizări ale echipei

2. Prospectare Proprietari
   - Pregătirea apelului
   - Scriptul complet de prospectare
   - Reguli de comunicare
   - Gestionarea obiecțiilor

3. Lucrul cu Chiriașii
   - Comisioane și servicii incluse
   - Procesul de calificare
   - Programarea vizionărilor
   - Închiderea tranzacției

4. Proceduri Administrative
   - Completarea Google Sheets
   - Indexarea în CRM
   - Statusuri și raportare
   - Consecințe pentru nerespectare

RESURSE ȘI PLATFORME:
- Platformă cursuri: pro.cautlocuinta.ro
- CRM pentru proprietăți și clienți
- Google Sheets pentru liste de prospectare
- Grup WhatsApp pentru suport zilnic
- Email suport: support@cautlocuinta.ro

REGULI IMPORTANTE:
- Niciun comision nu se încasează cash, doar prin factură
- Factura se emite DOAR după semnarea contractului
- Verificarea în CRM este OBLIGATORIE înainte de apel
- Completarea statusurilor în Google Sheets este OBLIGATORIE
- Comunicarea în grupul WhatsApp este ESENȚIALĂ

CONSECINȚE PENTRU NERESPECTARE:
- Neprelucrarea listei zilnice → Blocare generare listă nouă
- Neprelucrare consecutivă (2+ zile) → Blocare cont și audit
- Conversie sub 40% timp de 7 zile → Sesiune evaluare și re-training
- Lipsă comunicare cu suportul → Avertisment și raportare manager

TON ȘI STIL DE RĂSPUNS:
- Profesional dar prietenos și accesibil
- Folosește emoji-uri moderat pentru claritate (🎯 📊 ✅ ❌)
- Oferă exemple concrete când explici proceduri
- Structurează răspunsurile cu bullet points când e relevant
- Dacă întrebarea este despre informații foarte specifice pe care nu le ai, recomandă suportul live

CÂND SĂ REDIRECȚIONEZI CĂTRE SUPORT:
- Probleme tehnice cu CRM sau platformă
- Situații juridice sau fiscale complexe
- Cazuri speciale care necesită derogări
- Probleme urgente care necesită intervenție imediată

RĂSPUNDE DIRECT LA ÎNTREBĂRI DESPRE:
- Comisioane și calcule (ai toate formulele!)
- Structura carierei și nivelele
- Proceduri de prospectare și scriptul de apel
- Statusuri și completare Google Sheets
- Indexare în CRM
- Conținutul cursurilor
- Reguli și consecințe

Ești aici să ajuți agenții să înțeleagă și să aplice corect procedurile CautLocuinta.ro. Răspunde cu încredere la întrebările din domeniile tale de expertiză!`;
const PREDEFINED_QUESTIONS: PredefinedQuestion[] = [{
  id: '1',
  icon: '💰',
  question: 'Cum se calculează comisionul pentru proprietari?',
  category: 'Comisioane'
}, {
  id: '2',
  icon: '🏠',
  question: 'Cum se calculează comisionul pentru chiriași?',
  category: 'Comisioane'
}, {
  id: '3',
  icon: '📊',
  question: 'Care sunt nivelele de carieră și cum avansez?',
  category: 'Carieră'
}, {
  id: '4',
  icon: '📞',
  question: 'Cum fac un apel de prospectare către un proprietar?',
  category: 'Prospectare'
}, {
  id: '5',
  icon: '📋',
  question: 'Cum completez listele de prospectare?',
  category: 'Prospectare'
}, {
  id: '6',
  icon: '💻',
  question: 'Cum indexez o proprietate în CRM?',
  category: 'Proceduri'
}, {
  id: '7',
  icon: '🎓',
  question: 'Ce cursuri sunt disponibile pe platformă?',
  category: 'Training'
}, {
  id: '8',
  icon: '💬',
  question: 'Cum funcționează Suportul Individual CLC?',
  category: 'Suport'
}, {
  id: '9',
  icon: '⚠️',
  question: 'Ce se întâmplă dacă nu prelucră lista zilnică?',
  category: 'Reguli'
}, {
  id: '10',
  icon: '🔄',
  question: 'Când pot solicita o derogare de comision?',
  category: 'Proceduri'
}];
const PREDEFINED_ANSWERS: {
  [key: string]: string;
} = {
  'Cum se calculează comisionul pentru proprietari?': `💰 **Comisionul pentru Proprietari**
Comisionul standard pentru proprietari este:
📊 **Formula:**
• 50% din prima lună de chirie + TVA (21%)
💡 **Exemplu concret:**
Dacă chiria este 800€:
• Comision fără TVA: 400€ (50% din 800€)
• TVA (21%): 84€
• **Total comision: 484€**
✅ **Când se plătește:**
• DOAR după semnarea contractului de închiriere
• Nu se încasează cash, doar prin factură
• Factura se emite de departamentul financiar
📋 **Ce include serviciul:**
• Promovare proprietate în CRM
• Prezentare către clienți calificați
• Organizare vizionări
• Negociere condiții
• Asistență la semnarea contractului
💬 Pentru cazuri speciale sau derogări, contactează suportul în grupul WhatsApp!`,
  'Cum se calculează comisionul pentru chiriași?': `🏠 **Comisionul pentru Chiriași**
Comisionul standard pentru chiriași este:
📊 **Formula:**
• 50% din prima lună de chirie + TVA (21%)
💡 **Exemplu concret:**
Dacă chiria negociată este 600€:
• Comision fără TVA: 300€ (50% din 600€)
• TVA (21%): 63€
• **Total comision: 363€**
✅ **Servicii incluse:**
• Consultanță personalizată
• Selectarea proprietăților potrivite
• Programare și coordonare vizionări
• Negociere preț și condiții
• Verificare documente și contract
• Asistență completă la semnare
📋 **Când se plătește:**
• După semnarea contractului de închiriere
• Prin factură fiscală (nu cash)
• Procesare de către departamentul financiar
💬 Chiriașul plătește pentru expertiza și timpul economisit în căutare!`,
  'Care sunt nivelele de carieră și cum avansez?': `📊 **Structura de Carieră CautLocuinta.ro**
Avansarea în carieră se bazează pe numărul de tranzacții finalizate:
🎯 **1. Agent Junior (0-5 tranzacții)**
• Început de carieră, învățare intensivă
• Acces la toate cursurile și training-urile
• Suport dedicat pentru fiecare tranzacție
• Liste de prospectare zilnice

💼 **2. Agent (6-15 tranzacții)**
• Autonomie crescută în procesul de vânzare
• Bonusuri de performanță
• Acces la liste de prospectare prioritare
• Recunoaștere în echipă

🌟 **3. Agent Senior (16-30 tranzacții)**
• Expertiza validată și recunoscută
• Bonusuri majorate
• Posibilitate de mentorat pentru agenți junior
• Participare la proiecte speciale

👑 **4. Team Leader (31+ tranzacții)**
• Leadership și coordonare echipă
• Cele mai mari bonusuri și beneficii
• Participare la decizii strategice
• Dezvoltare profesională avansată

📈 **Cum avansezi:**
• Finalizează tranzacții constant
• Respectă procedurile și regulamentele
• Comunică activ în grupul de suport
• Participă la training-uri și cursuri

💡 Fiecare nivel aduce mai multe oportunități și beneficii!`,
  'Cum fac un apel de prospectare către un proprietar?': `📞 **Ghid Complet Apel de Prospectare**
**ÎNAINTE DE APEL:**
1️⃣ **Pregătire:**
• Vizualizează complet anunțul (text, poze, preț, zonă)
• Notează detalii importante (etaj, suprafață, dotări)
• Verifică în CRM dacă există deja (caută după telefon)
• Pregătește fișă pentru notițe

2️⃣ **Verificare CRM:**
• Dacă există colaborare activă (status roșu) → NU contacta
• Dacă nu există sau status inactiv → poți apela

**SCRIPTUL DE APEL:**
🔹 **Introducere:**
"Bună ziua! Mă numesc [Numele tău] și vă sun în legătură cu apartamentul dvs. de închiriat din zona ___. V-am prins într-un moment bun?"

🔹 **Explicație scurtă:**
"Vreau să fiu direct. Am clienți activi care caută în zona dvs. și proprietatea dvs. se potrivește perfect. Aș vrea să știu dacă aveți nevoie de ajutor sau dacă sunteți deschis la o colaborare punctuală?"

🔹 **Dacă e interesat:**
"Super! Comisionul este 50% + TVA din prima lună, se plătește doar dacă semnăm contractul. Nu vă cer acum niciun contract, ne vedem înainte de prima vizionare."

🔹 **Informații suplimentare:**
• Disponibilitate vizionări
• Cost întreținere
• Loc de parcare
• Centrală/termoficare
• Acceptă animale
• Tip chiriaș preferat

✅ **Reguli de aur:**
• Fii calm, politicos și direct
• Nu forța colaborarea
• Notează tot în CRM
• Respectă exact scriptul

💬 Pentru mai multe detalii, vezi cursul complet de Prospectare!`,
  'Cum completez listele de prospectare?': `📋 **Ghid Completare Liste de Prospectare**
**WORKFLOW COMPLET:**
1️⃣ **Accesează lista:**
• Deschide Google Sheet-ul alocat ție pentru ziua respectivă
• Verifică toate coloanele disponibile

2️⃣ **Verifică în CRM:**
• Caută numărul de telefon în sistem
• Dacă există colaborare activă (status roșu) → NU contacta
• Altfel → lead disponibil pentru apel

3️⃣ **Efectuează apelul:**
• Urmează scriptul standard de prospectare
• Fii profesional și politicos

4️⃣ **Completează statusul:**
Statusuri disponibile:
• ✅ **Colaborează** (necesită ID CRM obligatoriu!)
• ❌ **Nu colaborează**
• 📞 **Nu răspunde / mesaj trimis**
• ⚠️ **Număr invalid**
• 🏠 **A închiriat deja**
• 💼 **Dorește vânzare**
• 👥 **Colaborare coleg CLC**
• 💬 **Oferă comision redus** (cere derogare)

5️⃣ **Dacă proprietarul colaborează:**
• Indexează proprietatea în CRM
• Copiază ID-ul generat (ex: P14433)
• Inserează ID-ul în coloana "ID CRM"
• Bifează "Colaborare Preluată"

⚠️ **IMPORTANT:**
• Completarea este OBLIGATORIE pentru fiecare lead
• Necompletarea poate duce la blocare cont
• Profesionalismul se validează prin completare corectă

💡 Lead finalizat = Status "Colaborează" + ID CRM completat!`,
  'Cum indexez o proprietate în CRM?': `💻 **Ghid Indexare Proprietate în CRM**
**CÂND INDEXEZI:**
• DOAR când proprietarul acceptă colaborarea
• DUPĂ ce ai toate detaliile necesare
• ÎNAINTE de a marca lead-ul ca finalizat

**PAȘI DE INDEXARE:**
1️⃣ **Accesează CRM-ul:**
• Intră în secțiunea "Proprietăți Noi"
• Click pe "Adaugă Proprietate Nouă"

2️⃣ **Completează datele:**
📍 **Locație:**
• Adresă completă
• Zonă/cartier
• Proximitate transport/facilități

🏠 **Detalii proprietate:**
• Tip (apartament, casă, studio)
• Număr camere
• Suprafață (mp)
• Etaj
• An construcție

💰 **Condiții financiare:**
• Preț chirie lunar
• Cost întreținere
• Garanție necesară
• Utilități incluse

✨ **Dotări și facilități:**
• Mobilat/nemobilat
• Electrocasnice
• Centrală proprie/termoficare
• Aer condiționat
• Loc de parcare
• Balcon/terasă

👤 **Date proprietar:**
• Nume complet
• Telefon
• Email
• Preferințe chiriaș

3️⃣ **Salvează și copiază ID:**
• Sistemul generează ID unic (ex: P14433)
• Copiază ID-ul
• Inserează în Google Sheet la coloana "ID CRM"

4️⃣ **Bifează colaborare:**
• Marchează "Colaborare Preluată" în listă
• Verifică că toate datele sunt corecte

✅ **Verificare finală:**
• ID CRM completat în listă
• Status "Colaborează" marcat
• Toate detaliile salvate în CRM

💬 Pentru probleme tehnice, contactează suportul imediat!`,
  'Ce cursuri sunt disponibile pe platformă?': `🎓 **Cursuri Disponibile pe pro.cautlocuinta.ro**
**1. CURS INTRODUCTIV - FUNDAMENTELE**
📚 Conținut:
• Despre CautLocuinta.ro și cultura organizațională
• Structura carierei și comisioanele
• Recorduri și realizări ale echipei
• Viziune și valori

🎯 Pentru cine: Toți agenții noi (obligatoriu)
⏱️ Durată: ~45 minute

**2. PROSPECTARE PROPRIETARI**
📚 Conținut:
• Pregătirea apelului
• Scriptul complet de prospectare
• Reguli de comunicare
• Gestionarea obiecțiilor
• Verificare în CRM
• Indexare proprietăți

🎯 Pentru cine: Agenți care lucrează cu proprietari
⏱️ Durată: ~1.5 ore

**3. LUCRUL CU CHIRIAȘII**
📚 Conținut:
• Comisioane și servicii incluse
• Procesul de calificare
• Programarea vizionărilor
• Închiderea tranzacției
• Gestionarea documentelor

🎯 Pentru cine: Agenți care lucrează cu chiriași
⏱️ Durată: ~1 oră

**4. PROCEDURI ADMINISTRATIVE**
📚 Conținut:
• Completarea Google Sheets
• Indexarea în CRM
• Statusuri și raportare
• Consecințe pentru nerespectare
• Best practices

🎯 Pentru cine: Toți agenții (obligatoriu)
⏱️ Durată: ~1 oră

**5. SUPORT INDIVIDUAL CLC**
📚 Conținut:
• Cum funcționează grupul de suport
• Când și cum să comunici
• Tipuri de probleme și soluții
• Reguli de comunicare

🎯 Pentru cine: Toți agenții
⏱️ Durată: ~30 minute

✅ **Acces:**
• Toate cursurile sunt disponibile 24/7
• Progres salvat automat
• Certificate la finalizare
• Materiale descărcabile

💡 Recomandăm să parcurgi cursurile în ordinea listată pentru cea mai bună experiență de învățare!`,
  'Cum funcționează Suportul Individual CLC?': `💬 **Suportul Individual CLC - Ghid Complet**
**CE ESTE:**
Un sistem unic de asistență personalizată 7/7, oferit prin grupuri WhatsApp Business dedicate fiecărui agent.

**CINE FACE PARTE:**
👥 Grupul tău individual include:
• Membri ai Boardului
• Reprezentanți departamente specializate:
  - Legal
  - Tehnic
  - Fiscal
  - AI & Tehnologie
• Managerul tău direct
• Tu (agentul)

**PENTRU CE SITUAȚII:**
1️⃣ **Probleme zilnice:**
• Apeluri dificile
• Întrebări despre contracte
• Situații cu clienți
• Clarificări proceduri

2️⃣ **Întrebări despre proceduri:**
• Training-uri
• Acces platformă
• Procese interne
• Aplicare reguli

3️⃣ **Situații specializate:**
• Cazuri juridice complexe
• Aspecte fiscale
• Probleme tehnice CRM
• Consultanță experți

4️⃣ **Anunțuri personale:**
• Absențe obligatorii
• Concedii planificate
• Situații care afectează disponibilitatea

**CUM SĂ COMUNICI:**
✅ **Reguli de comunicare:**
• Mesaje clare și concise
• Începe cu subiectul problemei
• Răspunde rapid la solicitări
• Menține profesionalismul
• Evită discuții neproductive

⚠️ **Important:**
• Anunță orice absență
• Folosește grupul doar pentru comunicare de serviciu
• Comunicarea este OBLIGATORIE, nu opțională

**BENEFICII:**
🎯 **Dezvoltare accelerată:**
• Învățare asistată în timp real
• Rezolvare rapidă a problemelor
• Acces la expertiză specializată

📈 **Performanță îmbunătățită:**
• Eliminarea blocajelor
• Decizii mai rapide
• Suport constant

🤝 **Relație directă:**
• Acces la management
• Feedback imediat
• Transparență totală

💡 Fără comunicare constantă, sistemul nu poate interveni la timp pentru a te sprijini. Grupul este un instrument de lucru esențial!`,
  'Ce se întâmplă dacă nu prelucră lista zilnică?': `⚠️ **Consecințe pentru Neprelucrarea Listei Zilnice**
**SISTEM DE CONSECINȚE GRADUALE:**
🟡 **NIVEL 1 - Avertisment:**
**Situație:** Neprelucrezi lista zilnică fără motiv
**Consecință:**
• Blocare generare listă nouă
• Avertisment în grupul de suport
• Necesită explicație pentru deblocare

🟠 **NIVEL 2 - Blocare temporară:**
**Situație:** Neprelucrare consecutivă (2+ zile)
**Consecință:**
• Blocare cont CRM
• Audit de performanță obligatoriu
• Întâlnire cu managerul
• Plan de îmbunătățire

🔴 **NIVEL 3 - Evaluare intensivă:**
**Situație:** Conversie sub 40% timp de 7 zile
**Consecință:**
• Sesiune de evaluare detaliată
• Re-training obligatoriu
• Monitorizare intensivă
• Posibilă suspendare temporară

⚫ **NIVEL 4 - Sancțiuni severe:**
**Situație:** Lipsă comunicare constantă cu suportul
**Consecință:**
• Avertisment formal
• Raportare către management superior
• Posibilă încetare colaborare

**DE CE ACESTE REGULI:**
📊 **Profesionalism:**
• Listele sunt alocate zilnic cu resurse
• Alte persoane depind de procesarea ta
• Respectul pentru sistem = respect pentru echipă

💼 **Eficiență:**
• Listele neprelucrare = oportunități pierdute
• Alte agenți ar putea folosi acele lead-uri
• Timpul este valoros pentru toți

🎯 **Performanță:**
• Consistența este cheia succesului
• Prelucrarea zilnică dezvoltă obiceiuri bune
• Rezultatele vin din disciplină

**CUM EVIȚI PROBLEMELE:**
✅ **Prelucrează zilnic:**
• Dedică timp fix pentru liste
• Prioritizează această activitate
• Completează toate statusurile

✅ **Comunică proactiv:**
• Anunță din timp absențele
• Explică situațiile speciale
• Cere ajutor când ai nevoie

✅ **Respectă procedurile:**
• Verifică în CRM înainte de apel
• Completează corect statusurile
• Indexează proprietățile colaborate

💡 Sistemul este construit pentru succesul tău - respectă-l și vei avea tot suportul necesar!`,
  'Când pot solicita o derogare de comision?': `🔄 **Ghid Derogări de Comision**
**CE ESTE O DEROGARE:**
O excepție de la comisionul standard (50% + TVA) pentru cazuri speciale care justifică o reducere.

**CÂND POȚI SOLICITA:**
✅ **Cazuri acceptabile:**
1️⃣ **Proprietăți premium:**
• Chirii foarte mari (>2000€)
• Proprietar cu portofoliu mare
• Colaborare pe termen lung

2️⃣ **Situații competitive:**
• Alte agenții oferă comision mai mic
• Proprietar ezitant din cauza comisionului
• Risc de pierdere a lead-ului

3️⃣ **Clienți recurenți:**
• Proprietar cu care ai mai colaborat
• Recomandări din partea clientului
• Relație de încredere stabilită

4️⃣ **Cazuri speciale:**
• Închirieri pe termen foarte lung
• Multiple proprietăți același proprietar
• Situații urgente justificate

❌ **NU solicita derogare pentru:**
• Orice proprietate standard
• Doar pentru a încheia mai ușor
• Fără o justificare clară
• Când proprietarul nu a cerut reducere

**CUM SOLICIȚI:**
📝 **Proces:**
1️⃣ **Pregătește informațiile:**
• Link către proprietate
• Motivul clar și detaliat
• Procentul propus (ex: 40% în loc de 50%)
• Context și justificare

2️⃣ **Trimite cererea:**
• În grupul individual de suport CLC
• Format: "Solicit derogare pentru [link] - motiv: [explicație] - propun [X]%"
• Include toate detaliile relevante

3️⃣ **Așteaptă răspuns:**
• Echipa analizează cererea
• Răspuns în maxim 24h
• Decizie cu justificare

**EXEMPLE DE CERERI BUNE:**
✅ "Solicit derogare pentru https://... - Proprietate premium 2500€/lună, proprietar are 3 apartamente în portofoliu, dorește colaborare pe termen lung. Alte agenții oferă 40%. Propun 40% pentru a securiza colaborarea și viitoarele proprietăți."

✅ "Solicit derogare pentru https://... - Client recurent cu care am finalizat 2 tranzacții anterior, relație excelentă. Solicită 35% pentru această proprietate. Propun 40% pentru a menține relația."

❌ "Vreau derogare pentru https://... - proprietarul vrea mai puțin"

**IMPORTANT:**
⚠️ **Nu negocia singur:**
• Nu promite reduceri fără aprobare
• Nu te angajezi la un procent specific
• Spune: "Voi verifica cu echipa și revin"

📊 **Transparență:**
• Fii onest cu proprietarul
• Explică procesul de aprobare
• Comunică decizia prompt

💡 **Profesionalism:**
• Derogările sunt excepții, nu regula
• Comisionul standard este justificat de servicii
• Calitatea muncii tale merită compensația corectă

💬 Echipa evaluează fiecare cerere individual și va lua decizia cea mai bună pentru toți!`
};
export function AIAssistantChatProvider({
  children
}: {
  children: ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([{
    id: '1',
    role: 'assistant',
    content: 'Bună! 👋 Sunt asistentul AI Cautlocuință.ro.\n\nÎți pot răspunde la întrebări despre:\n\n🎓 Cursuri și training-uri\n💰 Comisioane și calcule\n📋 Proceduri de prospectare\n📊 Structura carierei\n🏠 Indexare proprietăți în CRM\n📞 Scripturi de apel\n\nSelectează o întrebare din lista de mai jos sau scrie-mi direct! 👇',
    timestamp: new Date()
  }]);
  const [isLoading, setIsLoading] = useState(false);
  const openChat = () => setIsOpen(true);
  const closeChat = () => setIsOpen(false);
  const toggleChat = () => setIsOpen(!isOpen);
  const sendMessage = async (content: string) => {
    if (!content.trim()) return;
    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);
    try {
      // Check if the message matches a predefined question
      const predefinedAnswer = PREDEFINED_ANSWERS[content.trim()];
      if (predefinedAnswer) {
        // Use predefined answer
        await new Promise(resolve => setTimeout(resolve, 500)); // Small delay for UX
        const assistantMessage: Message = {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: predefinedAnswer,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, assistantMessage]);
      } else {
        // Use OpenAI API for custom questions
        const apiKey = process.env.OPENAI_API_KEY;
        if (!apiKey || apiKey === 'undefined' || apiKey.trim() === '') {
          throw new Error('API key not configured');
        }
        const openai = new OpenAI({
          apiKey: apiKey,
          dangerouslyAllowBrowser: true
        });
        const conversationHistory = messages.map(msg => ({
          role: msg.role,
          content: msg.content
        }));
        const completion = await openai.chat.completions.create({
          model: 'gpt-4o-mini',
          messages: [{
            role: 'system',
            content: SYSTEM_PROMPT
          }, ...conversationHistory, {
            role: 'user',
            content: content
          }],
          temperature: 0.6,
          max_tokens: 800
        });
        const assistantResponse = completion.choices[0]?.message?.content || 'Ne pare rău, nu am putut genera un răspuns. Te rog să încerci din nou.';
        const assistantMessage: Message = {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: assistantResponse,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, assistantMessage]);
      }
    } catch (error) {
      console.error('OpenAI API Error:', error);
      let errorMessage = '';
      if (error instanceof Error && error.message.includes('API key not configured')) {
        errorMessage = '⚠️ **Chatbot-ul nu este configurat complet**\n\nPentru ca asistentul AI să funcționeze, trebuie să conectezi API key-ul OpenAI în panoul de **Integrări** din Magic Patterns.\n\n📞 Între timp, mă poți contacta direct la:\n\n• **Telefon**: 0733355359\n• **Email**: support@cautlocuinta.ro\n• **Program**: 09:00 - 19:00, 7 zile/săptămână\n\nÎți pot răspunde imediat la orice întrebare despre cursuri, comisioane sau proceduri! 💬';
      } else if (error instanceof Error && error.message.includes('rate limit')) {
        errorMessage = '⚠️ Am atins limita de cereri temporar.\n\nÎncearcă din nou în câteva momente sau contactează suportul live:\n\n📞 **0733355359** (09:00-19:00)';
      } else {
        errorMessage = '⚠️ Am întâmpinat o problemă tehnică.\n\nTe rog să contactezi suportul live pentru asistență imediată:\n\n📞 **0733355359**\n📧 **support@cautlocuinta.ro**';
      }
      const errorResponseMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: errorMessage,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorResponseMessage]);
    } finally {
      setIsLoading(false);
    }
  };
  const clearMessages = () => {
    setMessages([{
      id: '1',
      role: 'assistant',
      content: 'Bună! 👋 Sunt asistentul AI Cautlocuință.ro.\n\nÎți pot răspunde la întrebări despre:\n\n🎓 Cursuri și training-uri\n💰 Comisioane și calcule\n📋 Proceduri de prospectare\n📊 Structura carierei\n🏠 Indexare proprietăți în CRM\n📞 Scripturi de apel\n\nSelectează o întrebare din lista de mai jos sau scrie-mi direct! 👇',
      timestamp: new Date()
    }]);
  };
  return <AIAssistantChatContext.Provider value={{
    isOpen,
    messages,
    isLoading,
    openChat,
    closeChat,
    toggleChat,
    sendMessage,
    clearMessages,
    predefinedQuestions: PREDEFINED_QUESTIONS
  }} data-id="element-7100">
      {children}
    </AIAssistantChatContext.Provider>;
}
export function useAIAssistantChat() {
  const context = useContext(AIAssistantChatContext);
  if (!context) {
    throw new Error('useAIAssistantChat must be used within AIAssistantChatProvider');
  }
  return context;
}